import React, { useContext, useMemo, useState } from "react";
import { AiIcon, CancelIcon, ICON_SIZE } from "./actionIcons";
import { AlertTriangle, X } from "lucide-react";
import { GlobalContext } from "./GlobalContext";
import { emitter } from "./UtilityFunctions";
import { useListingHealth } from "./hooks/useListingHealth";
import { requestEnrich } from "./enrichIntent";

/**
 * "Some of your stock cannot be found by a buyer using the website filters."
 *
 * WHY A BANNER AND NOT JUST THE COLUMN. The Listing (%) column has shipped and
 * is rendered for everyone, but on a ~90-column sheet it lands wherever the
 * user's saved order puts it — for anyone who has ever reordered a column, at
 * the far right. A number nobody scrolls to does not get acted on.
 *
 * WHY IT CAN BE DISMISSED. A permanent bar that cannot be cleared is trained
 * away within a week, and then the one week it says something new is the week
 * nobody reads it. Dismissal is remembered per user and per browser, and is
 * deliberately NOT forever: it lapses after DISMISS_DAYS, and it lapses
 * immediately if the situation gets worse than it was when dismissed. Fixing
 * cars never brings it back — only adding unfilterable ones does.
 */

const DISMISS_DAYS = 7;
const DISMISS_KEY = (userId) => `listing-health-dismissed:${userId || "anon"}`;

function readDismissal(userId) {
    try {
        const raw = localStorage.getItem(DISMISS_KEY(userId));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.count === "number" && typeof parsed?.at === "number" ? parsed : null;
    } catch {
        //A malformed or unavailable localStorage must not take the banner down
        //with it — treat it as "never dismissed".
        return null;
    }
}

function writeDismissal(userId, count) {
    try {
        localStorage.setItem(DISMISS_KEY(userId), JSON.stringify({ count, at: Date.now() }));
    } catch {
        //Private mode / quota. The banner simply returns on the next load.
    }
}

export default function ListingHealthBanner({ sheetName = null }) {
    const { can, UserID } = useContext(GlobalContext);
    const allowed = can("inventory.edit");

    const { data } = useListingHealth({ enabled: allowed });
    const [dismissedNow, setDismissedNow] = useState(false);

    const incomplete = data?.incomplete || 0;
    const fillable = data?.fillable;

    const suppressed = useMemo(() => {
        if (!incomplete) return true;
        const d = readDismissal(UserID);
        if (!d) return false;
        //Got worse since they waved it away — that is new information.
        if (incomplete > d.count) return false;
        return Date.now() - d.at < DISMISS_DAYS * 86400000;
    }, [incomplete, UserID]);

    if (!allowed || !incomplete || suppressed || dismissedNow) return null;

    const dismiss = () => {
        writeDismissal(UserID, incomplete);
        setDismissedNow(true);
    };

    const openReview = () => {
        if (sheetName === "Inventory") {
            emitter.emit("open-enrich-specs");
            return;
        }
        //Not on Inventory: mount it first, and let it open the modal itself.
        requestEnrich();
        emitter.emit("navigate", { tab: "tab-inventory", subMenu: "Inventory" });
    };

    return (
        <div
            role="status"
            //Spotlighted by the Inventory tour. The dashboard renders a second
            //copy of this component, but the two never share a screen, so the
            //id stays unique in practice.
            id="listing-health-banner"
            style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                margin: "0 0 12px",
                border: "1px solid #fcd34d",
                //Semantic warning colours are hardcoded rather than themed, the
                //same decision the cell visuals and AGING_COLORS made: a tenant
                //whose accent happens to be amber must not lose the signal.
                background: "#fffbeb",
                borderRadius: 8,
                fontSize: 14,
                lineHeight: 1.45,
            }}
        >
            <AlertTriangle size={17} color="#b45309" style={{ flexShrink: 0, marginTop: 2 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: "#78350f" }}>
                    <strong>
                        {incomplete} {incomplete === 1 ? "vehicle" : "vehicles"}
                    </strong>{" "}
                    {incomplete === 1 ? "can't" : "can't"} be filtered by buyers on your website
                    {/* fillable is null when the lookup was unreachable. Saying
                        nothing is right: an invented number here would be a
                        promise the review screen then fails to keep. */}
                    {typeof fillable === "number" && fillable > 0 && (
                        <> — {fillable} of them fill in automatically.</>
                    )}
                    {typeof fillable === "number" && fillable === 0 && (
                        <> — these need details the vehicle lookup doesn't have.</>
                    )}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <button type="button" className="btn-t btn-t-primary" onClick={openReview}><AiIcon size={ICON_SIZE} />
                        Fill specs
                    </button>
                    <button type="button" className="btn-t btn-t-secondary" onClick={dismiss}><CancelIcon size={ICON_SIZE} />
                        Not now
                    </button>
                </div>
            </div>
            <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss"
                title={`Hide this for ${DISMISS_DAYS} days`}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#92400e", padding: 2, lineHeight: 0 }}
            >
                <X size={15} />
            </button>
        </div>
    );
}
